import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import App from "./App.jsx";
import { BrowserRouter } from "react-router-dom";
import { Provider } from "react-redux";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./utils/queryClient.js";
import { store, persistor, dropRetiredStorageKeys } from "./store/store.js";
import { startPersistenceWhenIdentityKnown } from "./store/storageOwner.js";
import { PersistGate } from "redux-persist/integration/react";
import "@ant-design/v5-patch-for-react-19";

import "./utils/clearStorage.js"; // dev-only window.clearPersistedStorage helper
import { installChunkErrorReload } from "./utils/chunkReload.js";
import { installTerminalAuthInterceptor } from "./utils/terminalAuthError.js";

// Recover from stale lazy-chunk 404s after a deploy (window-level, before render
// so it catches import() rejections regardless of React state).
installChunkErrorReload();
// One choke point for a server-issued "this session is over" refusal, so a
// deactivated user is signed out wherever their next request happens to be.
installTerminalAuthInterceptor();
// Drop localStorage blobs no build writes any more. The logout purge never
// reaches a user who closes the tab instead of signing out, so retired keys are
// cleared here on every load.
dropRetiredStorageKeys();
// Hold storage reads until Firebase says who is here, so one person's filters
// and workspace cannot rehydrate into another's session. An unreachable Firebase
// hydrates unchecked after a bounded wait, and clears when it does answer,
// because a blank app is worse than a stale first paint. Outside React, so
// StrictMode's double-invoked effects cannot subscribe twice.
startPersistenceWhenIdentityKnown(persistor, store);

createRoot(document.getElementById("root")).render(
  <BrowserRouter>
    <StrictMode>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <QueryClientProvider client={queryClient}>
            <App />
          </QueryClientProvider>
        </PersistGate>
      </Provider>
    </StrictMode>
  </BrowserRouter>
);
